import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface SEO {
  _id: string;
  page_url: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
}

const SEOPage: React.FC = () => {
  const navigate = useNavigate();
  const [seoList, setSeoList] = useState<SEO[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Base URL from .env
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000/api/v1";

  useEffect(() => {
    const fetchSEO = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/seo`);
        setSeoList(res.data);
      } catch (err) {
        console.error("Error fetching SEO data:", err);
        alert("❌ Failed to fetch SEO entries!");
      } finally {
        setLoading(false);
      }
    };
    fetchSEO();
  }, [API_BASE_URL]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this SEO entry?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/seo/${id}`);
      setSeoList((prev) => prev.filter((seo) => seo._id !== id));
      alert("✅ SEO entry deleted!");
    } catch (err) {
      console.error("Error deleting SEO entry:", err);
      alert("❌ Failed to delete SEO entry!");
    }
  };

  // Pagination + search logic remains same
  const filteredSEO = seoList.filter(
    (seo) =>
      seo.page_url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seo.meta_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seo.meta_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seo.meta_keywords.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalEntries = filteredSEO.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + entriesPerPage, totalEntries);
  const currentSEO = filteredSEO.slice(startIndex, endIndex);

  if (loading) return <div className="p-5 text-center">Loading...</div>;

  return (
    <div className="main-container container-fluid">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active">Manage SEO</li>
          </ol>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/settings/seo/add")}>
          <i className="icon-plus"></i> Add SEO
        </button>
      </div>

      <div className="card table-card">
        <div className="card-body">
          <div className="d-flex justify-content-between mb-3">
            <div>
              Show{" "}
              <select
                className="form-select form-select-sm d-inline-block"
                style={{ width: "auto" }}
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </div>
            <div>
              Search:{" "}
              <input
                type="search"
                className="form-control form-control-sm d-inline-block"
                style={{ width: "200px" }}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <table className="table table-striped table-bordered">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Page URL</th>
                <th>Meta Title</th>
                <th>Meta Description</th>
                <th>Meta Keywords</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentSEO.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">No data available</td>
                </tr>
              ) : (
                currentSEO.map((seo, index) => (
                  <tr key={seo._id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{seo.page_url}</td>
                    <td>{seo.meta_title}</td>
                    <td>{seo.meta_description}</td>
                    <td>{seo.meta_keywords}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => navigate(`/settings/seo/edit/${seo._id}`)}
                      >
                        <i className="fe fe-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(seo._id)}
                      >
                        <i className="fe fe-trash-2"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Showing {startIndex + 1} to {endIndex} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {Array.from({ length: totalPages }).map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOPage;

